import { useMemo } from "react";
import OrderHeaderTickIcon from "../../assets/svg/OrderHeaderTickIcon";
import dataConstants from "../../constants/dataConstants";
import useQueryParams from "../../services/utils/custom hooks/QueryHelper";
import useMobile from "../../utils/helpers/useMobile";
import MobileStatusHeader from "./MobileStatusHeader";

interface StatusHeaderProps {
  status: number;
  selectedTab?: string;
}

interface StatusItemProps {
  label: string;
  valid: boolean;
  hideRightBorder: boolean;
  isCancel: boolean;
  isCurrentStep?: boolean;
}

function StatusItem({
  label,
  valid,
  hideRightBorder,
  isCancel,
  isCurrentStep,
}: Readonly<StatusItemProps>): JSX.Element {
  return (
    <div className="h-[90px] block w-full">
      <div className="xl:mr-0 xl:ml-0 -mr-5 ml-5">
        <div className="flex items-center ">
          <div
            className={`w-[20px] flex flex-col items-center justify-center h-[20px] border rounded-full ${
              valid
                ? "border-primary bg-primary"
                : isCurrentStep
                  ? "border-primary"
                  : "border-dropDownBorder"
            }`}
          >
            {valid ? (
              <OrderHeaderTickIcon stroke="white" />
            ) : isCurrentStep ? (
              <div className="h-[8px] w-[8px] bg-primary rounded-full" />
            ) : (
              <OrderHeaderTickIcon stroke={undefined} />
            )}
          </div>
          {!hideRightBorder && (
            <div
              className={`h-[1px] w-[calc(100%_-_20px)] ${
                valid ? "bg-primary" : "bg-roundedInputBorder"
              }`}
            />
          )}
        </div>
      </div>
      <div className="w-full xl:-ml-7 ">
        <p
          className={`!text-sm max-w-[80px] mt-2 text-center ${
            isCancel ? "text-[#DCDFE4]" : "text-royalBlue"
          } xl:!text-base !text-xs`}
        >
          {label}
        </p>
      </div>
    </div>
  );
}

function StatusHeader({
  status,
  selectedTab,
}: Readonly<StatusHeaderProps>): JSX.Element {
  const query = useQueryParams();
  const isMobile = useMobile();

  const statuses = useMemo(() => {
    const base = dataConstants.status.map((option) => {
      const statusValues = query.getAll("status[]");
      if (statusValues.includes(option.value.toString())) {
        return { ...option, selected: true };
      }
      return { ...option };
    });

    const submittedIndex = base.findIndex((item) => item.value === 40);
    const isPendingAuthCurrent = status === 55 || status === 56;

    const foundIndex = isPendingAuthCurrent
      ? submittedIndex
      : base.findIndex((item) => item.value === status);

    return base.map((item, index) => ({
      ...item,
      valid: foundIndex >= 0 && index <= foundIndex,
    }));
  }, [status, query]);

  if (isMobile) {
    return <MobileStatusHeader statuses={statuses} />;
  }

  return (
    <div
      className={`${
        selectedTab !== "additional-info" &&
        "lg:sticky lg:top-0 bg-white py-5 mt-2 lg:z-20 border border-white h-32 overflow-x-auto w-full overflow-y-hidden no-scrollbar"
      }`}
    >
      <div className="flex items-center justify-center lg:pl-10">
        {statuses.map((step, index) => (
          <StatusItem
            label={step.label}
            hideRightBorder={step.label === "Completed"}
            valid={step.valid}
            isCurrentStep={!step.valid && statuses[index - 1]?.valid}
            key={step.label}
            isCancel={false}
          />
        ))}
      </div>
    </div>
  );
}

export default StatusHeader;
